import { MethodType, ParamsFetchType, GenerateUrlApiType, ResponseType } from 'src_common/browser/apiUtils';
import qs from 'query-string';
import { GenerateUrlApiParamsType } from 'src_common/server/webDriver/sdkApiWebUtils';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';

const FreeSpinTransactionsDataItemTypeIO = t.interface({
    id: t.string,
    externalId: t.string,
    universe: t.string,
    accountId: t.number,
    bonusId: t.string,
    bonusType: t.string,
    isBonusBalance: t.boolean,
    provider: t.string,
    gameId: t.string,
    roundId: t.number,
    transactionType: t.string,
    amount: t.number,
    operatorAmount: t.number,
    createdAt: t.string,
});

export type FreeSpinTransactionsDataItemType = t.TypeOf<typeof FreeSpinTransactionsDataItemTypeIO>;

const isFreeSpinTransactionsDataItemType = createGuard(FreeSpinTransactionsDataItemTypeIO);

const FreeSpinTransactionsResponseIO = t.interface({
    cursor: t.union([t.string, t.null]),
    hasMore: t.boolean,
    transactions: t.unknown,
});

export type FreeSpinTransactionsResponseDataType = t.TypeOf<typeof FreeSpinTransactionsResponseIO> & {
    transactions: Array<FreeSpinTransactionsDataItemType>;
};
const isFreeSpinTransactionsResponseType = createGuard(FreeSpinTransactionsResponseIO);

const isArray = createGuard(t.array(t.unknown));

const decodeModel = (model: unknown): FreeSpinTransactionsDataItemType | null => {
    if (!isFreeSpinTransactionsDataItemType(model)) {
        console.error('expected model FreeSpinTransactionsDataItem', model);
        return null;
    }

    return {
        ...model,
    };
};

const decode = (status: number, data: ResponseType): FreeSpinTransactionsResponseDataType => {
    if (status === 200 && data.type === 'json') {
        if (!isFreeSpinTransactionsResponseType(data.json)) {
            throw Error('Expected FreeSpinTransactionsResponseType model');
        }
        const model = data.json;
        const transactionsArr = model.transactions;

        if (!isArray(transactionsArr)) {
            throw Error('Expected array of transactions');
        }

        const out: Array<FreeSpinTransactionsDataItemType> = [];
        for (const transaction of transactionsArr) {
            const decodedModel = decodeModel(transaction);
            if (decodedModel !== null) {
                out.push(decodedModel);
            }
        }

        return {
            ...model,
            transactions: out,
        };
    }

    throw Error(`getFreeSpinTransactions -> Unsupported response code=${status}`);
};

export interface FreeSpinParamsType {
    userId: number;
    perPage: number;
    startDate: string;
    endDate: string;
    cursor: string | null;
}

export const getFreeSpinTransactions = {
    browser: {
        params: (params: FreeSpinParamsType): ParamsFetchType<FreeSpinParamsType> => {
            return {
                type: MethodType.POST,
                url: '/api-web/free-spin-transactions',
                body: params,
            };
        },
        decode,
    },
    express: {
        method: MethodType.POST,
        urlBrowser: '/api-web/free-spin-transactions',
    },
    generateUrlApi: async (params: GenerateUrlApiParamsType<FreeSpinParamsType>): Promise<GenerateUrlApiType> => {
        if (params.userSessionId === null) {
            return {
                passToBackend: false,
                status: 403,
                responseBody: {
                    errorMessage: 'User id invalid',
                },
            };
        }

        const { perPage, userId, startDate, endDate, cursor } = params.req.body;

        const query = {
            createdAfter: startDate,
            createdBefore: endDate,
            limit: perPage,
            cursor,
        };

        const queryParams = qs.stringify(query);
        const finalUrl = `${params.API_URL}/bonus-engine-api/bonuses/transactions/${params.API_UNIVERSE}/${userId}?${queryParams}`;

        return {
            url: finalUrl,
            passToBackend: true,
            method: MethodType.GET,
        };
    },
};
